import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { WidgetCard } from 'App/dashboards/_components/WidgetCard'
import { downloadCsv } from 'App/dashboards/_utils/dashboardDownloads'
import { useStickyOffset } from 'App/dashboards/_utils/stickyOffsetContext'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { colors, dashboardColors } from 'settings/colors'

export type TGroupedTableColumn<Row> = {
  key: string
  header: string
  // Plain text value — used for the CSV export and the collapse-repeats comparison.
  value: (row: Row) => string
  // Optional rich cell renderer; defaults to the plain `value` text.
  render?: (row: Row) => ReactNode
  // Blank the cell when identical to the row above, giving a group-label "rowspan" look. Use on the
  // leading group column (Zone / Emitter / Device). Never collapses in the CSV (full values export).
  // Contract: the caller must supply rows with same-group rows contiguous (i.e. pre-sorted by the
  // group column) — it only compares against the immediately preceding row. With several group
  // columns, a later one only blanks while every group column before it repeats too.
  collapseRepeats?: boolean
}

// One property = one accordion section.
export type TGroupedTableSection<Row> = {
  propertyId: number
  propertyName: string
  rows: Row[]
  // Per-property access (access is always per-property). When false, this property's section shows a
  // "no access" message instead of its rows / "no data". Defaults to accessible when omitted.
  hasAccess?: boolean
}

const getCollapsedColumnKeys = <Row,>(columns: TGroupedTableColumn<Row>[], row: Row, previous?: Row) => {
  const keys = new Set<string>()

  if (!previous) return keys

  for (const column of columns) {
    if (!column.collapseRepeats) continue

    if (column.value(row) !== column.value(previous)) break

    keys.add(column.key)
  }

  return keys
}

const headerCellCss = {
  textAlign: 'left' as const,
  padding: '12px 16px',
  fontSize: 14,
  fontWeight: 600,
  color: dashboardColors.columnHeaderText,
}

const bodyCellCss = {
  textAlign: 'left' as const,
  padding: '12px 16px',
  fontSize: 14,
  fontWeight: 500,
  color: colors.midnight,
  overflowWrap: 'break-word' as const,
  borderTop: `1px solid ${colors.grey200}`,
  verticalAlign: 'top' as const,
}

// Whether a section has a table to show, independent of widget-wide state. Shared so the collapse
// default and the sink order can't drift apart. NOTE: row counts are derived from the field-asset
// store, not the fetch — a property with devices but no readings in the window still yields a full set
// of rows with null values, so it counts as having rows and stays expanded at the top.
const sectionHasRows = <Row,>(section: TGroupedTableSection<Row>) =>
  section.hasAccess !== false && section.rows.length > 0

// A single property's collapsible section + its table. Open when it has a table to show; a property
// that resolves to a message instead (no access, a widget-wide error, or no devices/zones of this
// type) starts collapsed and sorts last. Every selected property still renders, so none is silently
// dropped from the widget.
const GroupedTableSection = <Row,>({
  section,
  columns,
  emptyMessage,
  error,
  loading,
}: {
  section: TGroupedTableSection<Row>
  columns: TGroupedTableColumn<Row>[]
  emptyMessage: string
  error?: Error | null
  loading: boolean
}) => {
  // A section that resolves to a message rather than a table starts collapsed — it contributes one
  // line either way, and leaving it open pushes the properties that DO have data off-screen.
  // Derived from current state rather than captured at mount. WaterMonitoringTable does the same thing
  // with `useState(section.zones.length > 0)`, which is a real mount-time snapshot: its sections are
  // store-derived and render unconditionally, so if properties and their zones arrive in separate store
  // updates a section can snapshot false and stay collapsed with data in it. This pattern is the one to
  // port there. `userOpen` records an explicit click, which then wins over the derived default.
  const [userOpen, setUserOpen] = useState<boolean | null>(null)
  // This property's banner pins under the card header, and its own column headers pin under the
  // banner. The banner's height is measured because it wraps differently by property name and width.
  const stickyOffset = useStickyOffset()
  const bannerRef = useRef<HTMLButtonElement>(null)
  const [bannerHeight, setBannerHeight] = useState(0)

  useLayoutEffect(() => {
    const element = bannerRef.current

    if (stickyOffset === null || !element) return

    const measure = () =>
      setBannerHeight((previous) => {
        const next = element.offsetHeight

        return previous === next ? previous : next
      })

    measure()

    const observer = new ResizeObserver(measure)

    observer.observe(element)

    return () => observer.disconnect()
  }, [stickyOffset])

  const pinned = stickyOffset !== null
  const width = `${100 / columns.length}%`
  // Per-section body precedence: no access (per-property) → fetch error → no data → table.
  const noAccess = section.hasAccess === false
  const showRows = !error && sectionHasRows(section)
  // Row counts don't change when data lands (they come from the store), so this can't flip mid-load on
  // its own — but `hasAccess` and `error` can still resolve late, so stay open while loading rather
  // than collapsing and re-expanding.
  const open = userOpen ?? (loading || showRows)

  let message = emptyMessage

  if (noAccess) {
    message = translate.phrases.banyanApp('You do not have access to this feature')
  } else if (error) {
    message = translate.phrases.banyanApp('Unable to load data. Try refreshing.')
  }

  return (
    <div
      css={{
        border: `1px solid ${colors.grey200}`,
        borderRadius: 8,
        // Same trap as the card: clipping the corners here makes this the scrollport for the banner
        // and column headers, so neither would pin. With them pinned the radius moves to the banner.
        ...(pinned ? {} : { overflow: 'hidden' }),
      }}
    >
      <button
        ref={bannerRef}
        onClick={() => setUserOpen(!open)}
        aria-expanded={open}
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          width: '100%',
          background: colors.grey50,
          border: 'none',
          borderBottom: open ? `1px solid ${colors.grey200}` : 'none',
          padding: '12px 16px',
          cursor: 'pointer',
          textAlign: 'left',
          color: colors.midnight,
          fontSize: 15,
          fontWeight: 600,
          ...(pinned
            ? { position: 'sticky', top: stickyOffset, zIndex: 2, borderRadius: '8px 8px 0 0' }
            : {}),
        }}
      >
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} size="sm" />
        {section.propertyName}
      </button>

      {open && !showRows && (
        <div css={{ padding: 16, textAlign: 'center', color: colors.grey800, fontSize: 14 }}>{message}</div>
      )}

      {open && showRows && (
        <table css={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  css={{
                    ...headerCellCss,
                    ...(pinned
                      ? {
                          position: 'sticky',
                          top: (stickyOffset ?? 0) + bannerHeight,
                          zIndex: 1,
                          background: colors.white,
                        }
                      : {}),
                  }}
                  style={{ width }}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row, rowIndex) => {
              const collapsedKeys = getCollapsedColumnKeys(columns, row, section.rows[rowIndex - 1])

              return (
                <tr key={rowIndex}>
                  {columns.map((column) => {
                    // Use the rich renderer whenever one is supplied (even if it returns null, so a
                    // renderer can deliberately render an empty cell); otherwise the plain text value.
                    const cell = column.render ? column.render(row) : column.value(row)

                    return (
                      <td key={column.key} css={{ ...bodyCellCss, width }}>
                        {collapsedKeys.has(column.key) ? '' : cell}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

type TDashboardGroupedTableProps<Row> = {
  title: string
  sections: TGroupedTableSection<Row>[]
  columns: TGroupedTableColumn<Row>[]
  loading?: boolean
  // Show the "N Properties" count chip (= number of sections).
  showPropertyCount?: boolean
  // When set, enables CSV download with this base filename (the export date is appended).
  downloadFileName?: string
  // Centered message shown when there are no rows across all sections.
  emptyMessage?: string
  // When the fetch failed: surfaced per-section as an error message (instead of an empty state).
  error?: Error | null
}

// Shared grouped-table widget for the water dashboards: a WidgetCard wrapping one collapsible section
// per property, a property-count chip, and a CSV export. Each widget supplies its own column defs +
// per-property rows; every column always renders, so the table body and the CSV can't disagree.
export const DashboardGroupedTable = <Row,>({
  title,
  sections,
  columns,
  loading = false,
  showPropertyCount = false,
  downloadFileName,
  emptyMessage,
  error,
}: TDashboardGroupedTableProps<Row>) => {
  const displayedColumns = columns
  const hasAnyRows = sections.some((s) => s.rows.length > 0)
  // Always render one section per selected property; each section owns its own message (no-access /
  // error / no-data), since access is per-property. The only widget-level state is "nothing selected".
  const showSections = sections.length > 0
  const resolvedEmptyMessage = emptyMessage ?? translate.phrases.banyanApp('No data to display')

  // Properties that resolve to a message rather than a table sink to the bottom, so the ones worth
  // reading stay at the top. Stable within each group, so the property picker's ordering still decides
  // the rest. The order can't shuffle when data lands because row counts are store-derived and so
  // data-independent — not because sections start empty. Mirrors WaterMonitoringTable's orderedSections.
  //
  // Deliberately keyed on `sectionHasRows` alone, unlike `showRows`, which also folds in `error`. A
  // fetch error is widget-wide, so it makes every section message-only at once: reordering on it would
  // reshuffle the whole list the moment an error appeared and shuffle it back on retry, for no gain.
  const orderedSections = useMemo(() => {
    const withRows = sections.filter(sectionHasRows)
    const withoutRows = sections.filter((s) => !sectionHasRows(s))

    return [...withRows, ...withoutRows]
  }, [sections])

  const handleDownload = useCallback(() => {
    if (!downloadFileName) return

    const headers = [translate.phrases.banyanApp('Property'), ...displayedColumns.map((c) => c.header)]

    const csvRows = sections.flatMap((section) =>
      section.rows.map((row) => [section.propertyName, ...displayedColumns.map((c) => c.value(row))]),
    )

    // Stamp the export date — the totals are relative to the selected window, so the file is only
    // interpretable later if you know when it was taken (matches the trap-counts export).
    const today = translate.dates.format(moment.tz(moment.tz.guess()), 'MMM D, YYYY')

    downloadCsv(`${downloadFileName} - ${today}.csv`, headers, csvRows)
  }, [downloadFileName, displayedColumns, sections])

  return (
    <WidgetCard
      title={title}
      count={showPropertyCount ? sections.length : undefined}
      loading={loading}
      onDownload={
        showSections && hasAnyRows && !error && downloadFileName && !loading ? handleDownload : undefined
      }
    >
      {showSections ? (
        <div css={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {orderedSections.map((section) => (
            <GroupedTableSection
              key={section.propertyId}
              section={section}
              columns={displayedColumns}
              emptyMessage={resolvedEmptyMessage}
              error={error}
              loading={loading}
            />
          ))}
        </div>
      ) : (
        <div
          css={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            minHeight: 80,
            padding: 16,
            color: colors.grey800,
            fontSize: 14,
          }}
        >
          {resolvedEmptyMessage}
        </div>
      )}
    </WidgetCard>
  )
}
